import { useMemo } from 'react';

import { type Board, type Clip } from '@air/api/types';
import { getFilename } from '@air/component-file-extension-preview';

import { DndDragPreviewContainer } from '~/components/DndDragPreviewContainer';
import { ClipThumbnail } from '~/components/Shared/ClipList/ClipItem/ClipThumbail';
import DragType, { type DraggingItems } from '~/components/Shared/Drag/dragTypes';
import { ClipIconSize } from '~/types/clip-icon-size';
import { AssetCellThumbnail } from '../TableView/NameCell/AssetCellThumbnail';
import { BoardCellThumbnail } from '../TableView/NameCell/BoardCellThumbnail';
import { DEFAULT_THUMBNAIL_DIMENSIONS } from '../TableView/NameCell/dimensions';
import { ReactDndDragItemsCount } from './shared/ReactDndDragItemsCount';

type TableItemsDragPreviewProps = {
  items: DraggingItems<Board | Clip>;
};

export const TableItemsDragPreview = ({
  items: { type, count, firstItem, mixedTypes },
}: TableItemsDragPreviewProps) => {
  const itemTitle = useMemo(() => {
    switch (type) {
      case DragType.boardNavFavorites:
      case DragType.boardNavWorkspace:
      case DragType.boardNavLibrary:
      case DragType.boardCard:
        return (firstItem as Board).title;
      case DragType.asset: {
        return firstItem.title || (firstItem as Clip).importedName || '';
      }
      case DragType.file: {
        return getFilename(firstItem as Clip);
      }
    }
  }, [firstItem, type]);

  const itemImage = useMemo(() => {
    switch (type) {
      case DragType.boardNavFavorites:
      case DragType.boardNavWorkspace:
      case DragType.boardCard: {
        const board = firstItem as Board;

        return <BoardCellThumbnail thumbnail={board.thumbnails?.[0]} title={board.title} />;
      }
      case DragType.asset: {
        const asset = firstItem as Clip;
        return <AssetCellThumbnail clip={asset} description={asset.title || asset.importedName || 'Untitled'} />;
      }
      case DragType.file: {
        const file = firstItem as Clip;
        return (
          <ClipThumbnail
            {...file}
            hideText={true}
            src=""
            iconSize={ClipIconSize.small}
            borderRadius={4}
            size="small"
            thumbnailPreference="default"
          />
        );
      }
    }
  }, [type, firstItem]);

  const positionOffset = count > 1 ? 6 : 0;

  return (
    <div className="relative">
      {/*Height must be set in style, because it is set in style props in DndDragPreviewContainer*/}
      {count > 2 && (
        <DndDragPreviewContainer className="absolute left-4" style={{ top: 16 + positionOffset, height: 64 }} />
      )}
      {count > 1 && (
        <DndDragPreviewContainer className="absolute left-2.5" style={{ top: 10 + positionOffset, height: 64 }} />
      )}
      <DndDragPreviewContainer
        className="absolute left-1 flex items-center p-3"
        style={{ top: 4 + positionOffset, height: 64 }}
      >
        <div
          className="relative"
          style={{
            ...DEFAULT_THUMBNAIL_DIMENSIONS,
            minWidth: DEFAULT_THUMBNAIL_DIMENSIONS.width,
            minHeight: DEFAULT_THUMBNAIL_DIMENSIONS.height,
          }}
        >
          {itemImage}
        </div>
        <div className="ml-3 truncate font-medium text-16 text-grey-12">{itemTitle}</div>
      </DndDragPreviewContainer>
      {count > 1 && (
        <div className="absolute top-[-18px] left-1">
          <ReactDndDragItemsCount type={type} mixedTypes={mixedTypes} count={count} />
        </div>
      )}
    </div>
  );
};
